import type { Editor } from "@tiptap/core";
import { useCallback, useLayoutEffect, useRef, type RefObject } from "react";
import type { DirectiveChoiceSlot } from "../../directives/core/config";
import { shouldChipShowChoiceUI } from "../../directives/suggestion/storage/floatingStorage";
import type { DirectiveChipEditSegment } from "../../types/directiveChipTypes";
import { focusChoiceControlAfterSync } from "../../directives/builder/interaction/focusChoice";

type BooleanRef = { current: boolean };

type Args = {
  editor: Editor;
  getPos: () => number | undefined;
  editSegment: DirectiveChipEditSegment | null;
  needsVariable: boolean;
  needsNextChoice: boolean;

  open: boolean;
  choicesLength: number;
  nextChoiceSlot: DirectiveChoiceSlot | undefined;
  chipPickerKeyboardRef: RefObject<HTMLDivElement | null>;
  chipSegmentFilterInputRef: RefObject<HTMLInputElement | null>;
  choiceInputRef: RefObject<HTMLInputElement | null>;
  choiceSelectTriggerRef: RefObject<HTMLButtonElement | null>;
  shouldFocusFirstSlotRef: BooleanRef;
  focusChoiceInputOnceRef: BooleanRef;
  focusChoiceSelectOnceRef: BooleanRef;
};

const useFocusChoiceOnceWhenFlag = (
  needsNextChoice: boolean,
  choicesLength: number,
  onceRef: BooleanRef,
  getElement: () => HTMLElement | null,
) => {
  useLayoutEffect(() => {
    if (!needsNextChoice || !onceRef.current) return;
    let cancelFocus: (() => void) | undefined;
    const runFocus = () => {
      const el = getElement();
      if (el == null) return false;
      onceRef.current = false;
      cancelFocus = focusChoiceControlAfterSync(el);
      return true;
    };
    if (runFocus()) {
      return () => {
        cancelFocus?.();
      };
    }
    const id = requestAnimationFrame(() => {
      runFocus();
    });
    return () => {
      cancelAnimationFrame(id);
      cancelFocus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ref objects stable; focus when choice count advances
  }, [needsNextChoice, choicesLength]);
};

export const useDirectiveChipPopoverFocus = ({
  editor,
  getPos,
  editSegment,
  needsVariable,
  needsNextChoice,
  open,
  choicesLength,
  nextChoiceSlot,
  chipPickerKeyboardRef,
  chipSegmentFilterInputRef,
  choiceInputRef,
  choiceSelectTriggerRef,
  shouldFocusFirstSlotRef,
  focusChoiceInputOnceRef,
  focusChoiceSelectOnceRef,
}: Args) => {
  const prevChoicesLenRef = useRef<number | null>(null);
  const prevNeedsVariableRef = useRef<boolean | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      prevNeedsVariableRef.current = null;
      return;
    }
    const wasPrev = prevNeedsVariableRef.current;
    const crossedToFirstColumn =
      wasPrev === true &&
      needsVariable === false &&
      needsNextChoice &&
      choicesLength === 0 &&
      nextChoiceSlot?.options != null;
    if (crossedToFirstColumn && shouldChipShowChoiceUI(editor, getPos, open)) {
      focusChoiceSelectOnceRef.current = true;
    }
    prevNeedsVariableRef.current = needsVariable;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- focusChoiceSelectOnceRef object is stable
  }, [
    open,
    needsVariable,
    needsNextChoice,
    choicesLength,
    nextChoiceSlot,
    editor,
    getPos,
  ]);

  useLayoutEffect(
    () => {
      if (!open) {
        prevChoicesLenRef.current = null;
        return;
      }
      if (!needsNextChoice || nextChoiceSlot == null) {
        prevChoicesLenRef.current = choicesLength;
        return;
      }
      const prev = prevChoicesLenRef.current;
      const gainedChoice =
        (prev !== null && choicesLength > prev) ||
        (prev === null && choicesLength > 0);
      if (gainedChoice && nextChoiceSlot != null) {
        if (nextChoiceSlot.options != null) {
          focusChoiceSelectOnceRef.current = true;
        } else {
          focusChoiceInputOnceRef.current = true;
        }
      }
      prevChoicesLenRef.current = choicesLength;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boolean refs stable; omit from deps
    [open, needsNextChoice, nextChoiceSlot, choicesLength],
  );
  const handlePopoverContentOpenAutoFocus = useCallback(
    (e: Event) => {
      const pickerMode =
        editSegment === "directive" ||
        editSegment === "variable" ||
        (editSegment === null && needsVariable);
      if (pickerMode) {
        e.preventDefault();
        requestAnimationFrame(() => {
          const input = chipSegmentFilterInputRef.current;
          if (input != null) {
            input.focus({ preventScroll: true });
          } else {
            chipPickerKeyboardRef.current?.focus();
          }
        });
        shouldFocusFirstSlotRef.current = false;
        return;
      }
      if (!shouldFocusFirstSlotRef.current) return;
      e.preventDefault();
      const choiceInp = choiceInputRef.current;
      const choiceSel = choiceSelectTriggerRef.current;
      const doFocus = () => {
        const sel = choiceSelectTriggerRef.current;
        const inp = choiceInputRef.current;
        if (needsNextChoice) {
          if (sel) sel.focus();
          else if (inp) inp.focus();
        }
        shouldFocusFirstSlotRef.current = false;
      };
      if (needsNextChoice && (choiceSel || choiceInp)) {
        doFocus();
      } else {
        requestAnimationFrame(doFocus);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs intentionally omitted (stable)
    [editSegment, needsVariable, needsNextChoice],
  );

  useFocusChoiceOnceWhenFlag(
    needsNextChoice,
    choicesLength,
    focusChoiceInputOnceRef,
    () => choiceInputRef.current,
  );
  useFocusChoiceOnceWhenFlag(
    needsNextChoice,
    choicesLength,
    focusChoiceSelectOnceRef,
    () => choiceSelectTriggerRef.current,
  );

  return { handlePopoverContentOpenAutoFocus };
};
